// Pure scheduling rules for the doctor arrival reminder (no React, no DOM
// except an injected Storage). Everything takes `now` (epoch ms) so the rules
// can be unit-tested without real timers.

import { TYPE_MANAGEMENT_QUEUE } from "@Constants/index";
import { DOCTOR_ARRIVAL_STORAGE_PREFIX } from "@Constants/doctor-arrival-reminder";

// The clinic runs on WITA (Asia/Makassar, UTC+8, no DST). shift.start_time and
// end_time are plain "HH:mm:ss" WITA strings, so elapsed time and the clock in
// the modal are computed in WITA whatever the admin PC's timezone is.
export const CLINIC_UTC_OFFSET_MINUTES = 8 * 60;
// Mirrors Queue::getSessionDate() on the backend: before 05:00 WITA the
// session still belongs to the previous day.
const SESSION_CUTOFF_MINUTES = 5 * 60;

const MINUTE_MS = 60 * 1000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

const pad2 = (n) => String(n).padStart(2, "0");

// A Date whose UTC fields read as the WITA wall clock.
const toClinicWallClock = (now) =>
  new Date(now + CLINIC_UTC_OFFSET_MINUTES * MINUTE_MS);

export const clinicSessionDate = (now) => {
  const wall = toClinicWallClock(now);
  if (wall.getUTCHours() * 60 + wall.getUTCMinutes() < SESSION_CUTOFF_MINUTES) {
    wall.setUTCDate(wall.getUTCDate() - 1);
  }
  return `${wall.getUTCFullYear()}-${pad2(wall.getUTCMonth() + 1)}-${pad2(
    wall.getUTCDate(),
  )}`;
};

// "HH:mm" of `now` in WITA.
export const formatClinicClock = (now) => {
  const wall = toClinicWallClock(now);
  return `${pad2(wall.getUTCHours())}:${pad2(wall.getUTCMinutes())}`;
};

// "16:00:00" -> "16:00"; anything unparseable is returned as-is.
export const formatShiftTime = (value) => {
  const match = TIME_RE.exec(String(value || "").trim());
  if (!match) return value ? String(value) : "";
  return `${pad2(match[1])}:${match[2]}`;
};

const isBlank = (value) => value === null || value === undefined || value === "";

/**
 * Normalises a getTodaysQueueInfo response into the shift the reminder cares
 * about, or null when there is no running Praktek shift with an id.
 * `arrived` mirrors doctor_arrival_status (falsy = not ticked yet).
 */
export const getReminderShift = (info, now) => {
  const shift = info && info.shift;
  if (!shift || isBlank(shift.id)) return null;
  if (shift.type && shift.type !== TYPE_MANAGEMENT_QUEUE.PRAKTEK_OFFLINE) {
    return null;
  }
  if (!isBlank(shift.is_running) && !Number(shift.is_running)) return null;

  const date =
    info.date && DATE_RE.test(String(info.date))
      ? String(info.date)
      : clinicSessionDate(now);

  return {
    id: shift.id,
    name: shift.name || "",
    startTime: shift.start_time || null,
    endTime: shift.end_time || null,
    date,
    arrived: Boolean(Number(shift.doctor_arrival_status)),
  };
};

/**
 * Whole minutes since the shift's start_time on its session date (WITA), or
 * null when start_time or the date can't be parsed.
 */
export const elapsedShiftMinutes = (startTime, date, now) => {
  const time = TIME_RE.exec(String(startTime || "").trim());
  const day = DATE_RE.exec(String(date || ""));
  if (!time || !day) return null;
  const [, h, m, s] = time;
  const startUtc =
    Date.UTC(+day[1], +day[2] - 1, +day[3], +h, +m, +(s || 0)) -
    CLINIC_UTC_OFFSET_MINUTES * MINUTE_MS;
  return Math.floor((now - startUtc) / MINUTE_MS);
};

// "sudah berjalan N menit", or null -> caller shows "baru saja dimulai".
export const elapsedMinutesLabel = (minutes) =>
  typeof minutes === "number" && Number.isFinite(minutes) && minutes > 0
    ? `${minutes} menit`
    : null;

// ---------------------------------------------------------------------------
// Persisted schedule: { nextDueAt, count, open } per shift id + session date.
// Shift rows are reused every day, so the date is part of the key.
// ---------------------------------------------------------------------------

export const reminderStorageKey = (shiftId, date) =>
  `${DOCTOR_ARRIVAL_STORAGE_PREFIX}:${shiftId}:${date}`;

export const isReminderStorageKey = (key) =>
  typeof key === "string" &&
  key.indexOf(`${DOCTOR_ARRIVAL_STORAGE_PREFIX}:`) === 0;

export const loadReminderState = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed.nextDueAt !== "number" ||
      !Number.isFinite(parsed.nextDueAt)
    ) {
      return null;
    }
    return {
      nextDueAt: parsed.nextDueAt,
      count: Number.isInteger(parsed.count) && parsed.count > 0 ? parsed.count : 0,
      open: Boolean(parsed.open),
    };
  } catch (e) {
    return null;
  }
};

export const saveReminderState = (storage, key, state) => {
  try {
    storage.setItem(key, JSON.stringify(state));
  } catch (e) {
    // Storage full / disabled: the reminder still works for this page view.
  }
};

export const clearReminderState = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch (e) {
    // ignore
  }
};

// Drops entries of earlier shifts / days so they don't pile up.
export const clearOtherReminderStates = (storage, keepKey) => {
  try {
    const stale = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (isReminderStorageKey(key) && key !== keepKey) stale.push(key);
    }
    stale.forEach((key) => storage.removeItem(key));
  } catch (e) {
    // ignore
  }
};

// A shift seen pending for the first time is due right away.
export const initialReminderState = (now) => ({
  nextDueAt: now,
  count: 0,
  open: false,
});

export const isReminderDue = (state, now) => !!state && now >= state.nextDueAt;

export const fireReminder = (state, now, intervalMs) => ({
  nextDueAt: now + intervalMs,
  count: (state ? state.count : 0) + 1,
  open: true,
});

export const snoozeReminder = (state, now, intervalMs) => ({
  nextDueAt: now + intervalMs,
  count: state ? state.count : 0,
  open: false,
});

/**
 * One scheduling step for a pending (running, unticked) shift: loads the saved
 * state (or starts fresh), fires when due and persists the result.
 * Returns { key, state, fired }.
 */
export const checkReminder = ({ storage, shift, now, intervalMs }) => {
  const key = reminderStorageKey(shift.id, shift.date);
  clearOtherReminderStates(storage, key);
  const current = loadReminderState(storage, key) || initialReminderState(now);
  if (isReminderDue(current, now)) {
    const state = fireReminder(current, now, intervalMs);
    saveReminderState(storage, key, state);
    return { key, state, fired: true };
  }
  return { key, state: current, fired: false };
};

/** Persists a snooze ("Belum, ingatkan 10 menit lagi") for `shift`. */
export const snoozeShiftReminder = ({ storage, shift, now, intervalMs }) => {
  const key = reminderStorageKey(shift.id, shift.date);
  const state = snoozeReminder(loadReminderState(storage, key), now, intervalMs);
  saveReminderState(storage, key, state);
  return state;
};
